/**
 * شريط تبويبات الكوليكشنات — يظهر أعلى صفحات المنتجات وصفحات المجموعات.
 * مستخرَج من FragranceTabsBlock ليُستعمل كملّاح ثانوي ثابت عبر الموقع.
 */
import * as React from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useLocale } from "@/contexts/LocaleContext";
import { cn } from "@/lib/utils";

type TabDef = {
  handle: string;
  labelKey: Parameters<ReturnType<typeof useLocale>["t"]>[0];
  /** رابط ثابت خارج نظام `/collection/$handle` (صفحة مستقلّة). */
  fixedPath?: string;
};

const ALL_HANDLE = "single-perfume";

/** مجموعة Shopify الخاصّة بالمنتجات الجديدة — تُدار من شوبيفاي كأيّ مجموعة. */
export const NEW_PRODUCT_COLLECTION = "new-product";

/** مسار صفحة «منتج جديد» المستقلّة (بلوكاتها من /admin/new-product). */
export const NEW_PRODUCT_PATH = "/new-product";

const TABS: TabDef[] = [
  // «كل العطور» = صفحة المتجر نفسها، لا مجموعة Shopify.
  { handle: ALL_HANDLE, labelKey: "nav.all_perfumes", fixedPath: "/shop" },
  { handle: "ladybag-collection", labelKey: "nav.lady_bag" },
  { handle: "gift-set-perfume-collection", labelKey: "nav.gift_sets" },
  { handle: NEW_PRODUCT_COLLECTION, labelKey: "nav.new_product", fixedPath: NEW_PRODUCT_PATH },
];

export function CollectionTabsNav({
  activeHandle,
  productCollectionHandles,
}: {
  activeHandle?: string;
  /** إن كنّا في صفحة منتج، مرِّر مجموعات المنتج ليُبرز التبويب الموافق. */
  productCollectionHandles?: string[];
}) {
  const { t } = useLocale();
  const location = useLocation();

  // إن لم يُمرَّر activeHandle، استنتجه من المسار الحالي أو من مجموعات المنتج
  const resolvedActive = React.useMemo(() => {
    if (activeHandle) return activeHandle;
    if (location.pathname.startsWith(NEW_PRODUCT_PATH)) return NEW_PRODUCT_COLLECTION;
    if (location.pathname === "/shop" || location.pathname === "/shop/") {
      return ALL_HANDLE;
    }
    const m = location.pathname.match(/\/collection\/([^/]+)/);
    if (m?.[1]) return m[1];
    // صفحة منتج: أوّل مجموعة موجودة في تبويباتنا
    if (productCollectionHandles?.length) {
      const set = new Set(productCollectionHandles.map((h) => h.toLowerCase()));
      const match = TABS.find((tab) => tab.handle !== ALL_HANDLE && set.has(tab.handle));
      if (match) return match.handle;
    }
    return undefined;
  }, [activeHandle, location.pathname, productCollectionHandles]);

  const tablistRef = React.useRef<HTMLDivElement | null>(null);
  const [scrollHint, setScrollHint] = React.useState({ start: false, end: false });

  React.useEffect(() => {
    const el = tablistRef.current;
    if (!el) return;
    const update = () => {
      const TOL = 4;
      const overflow = el.scrollWidth - el.clientWidth > TOL;
      if (!overflow) {
        setScrollHint({ start: false, end: false });
        return;
      }
      const fromStart = Math.abs(el.scrollLeft);
      const maxScroll = el.scrollWidth - el.clientWidth;
      const fromEnd = Math.max(0, maxScroll - fromStart);
      setScrollHint({ start: fromStart > TOL, end: fromEnd > TOL });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <section className="relative border-y border-foreground bg-background">
      <div
        ref={tablistRef}
        role="tablist"
        aria-label={t("section.collections")}
        className="flex min-h-[52px] items-center justify-start gap-5 overflow-x-auto whitespace-nowrap px-4 md:min-h-[64px] md:justify-center md:gap-[clamp(22px,3.5vw,64px)] md:px-7 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {TABS.map((tab) => {
          const active = tab.handle === resolvedActive;
          const commonClass = cn(
            "group relative shrink-0 py-4 text-[11px] tracking-[0.02em] uppercase transition-colors md:py-5 md:text-[13px]",
            active
              ? "text-foreground font-semibold"
              : "text-foreground/60 hover:text-foreground",
          );
          const underline = (
            <span
              aria-hidden
              className={cn(
                "pointer-events-none absolute bottom-2 h-px bg-current transition-[left,right] duration-300 ease-out md:bottom-3",
                active ? "left-0 right-0" : "left-1/2 right-1/2",
              )}
            />
          );
          if (tab.fixedPath) {
            return (
              <Link
                key={tab.handle}
                to={tab.fixedPath}
                aria-selected={active}
                role="tab"
                className={commonClass}
              >
                {t(tab.labelKey)}
                {underline}
              </Link>
            );
          }
          return (
            <Link
              key={tab.handle}
              to="/collection/$handle"
              params={{ handle: tab.handle }}
              aria-selected={active}
              role="tab"
              className={commonClass}
            >
              {t(tab.labelKey)}
              {underline}
            </Link>
          );
        })}

      </div>
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-background to-transparent transition-opacity duration-200 md:hidden",
          scrollHint.start ? "opacity-100" : "opacity-0",
        )}
      />
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent transition-opacity duration-200 md:hidden",
          scrollHint.end ? "opacity-100" : "opacity-0",
        )}
      />
    </section>
  );
}
